// src/adminDashboard/shared/components/AdvancedFilters/MultiSelect.tsx
// Multi-select dropdown with search and chips

import { ChevronDown, X, Check, Search } from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';

export interface MultiSelectOption {
    label: string;
    value: string | number;
    count?: number;
}

interface MultiSelectProps {
    label?: string;
    options?: MultiSelectOption[];
    value?: (string | number)[];
    onChange: (value: (string | number)[]) => void;
    onSearchChange?: (query: string) => void;
    placeholder?: string;
    searchable?: boolean;
    className?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
    label,
    options = [],
    value = [],
    onChange,
    onSearchChange,
    placeholder = 'Select options...',
    searchable = true,
    className = '',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search on open
    useEffect(() => {
        if (isOpen && searchable && searchRef.current) {
            searchRef.current.focus();
        }
    }, [isOpen, searchable]);

    // If onSearchChange is provided, we assume the parent handles filtering (server-side)
    // Otherwise, we filter locally
    const filteredOptions = onSearchChange
        ? options
        : options.filter((option) =>
            option.label.toLowerCase().includes(searchQuery.toLowerCase())
        );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchQuery(query);
        if (onSearchChange) {
            onSearchChange(query);
        }
    };

    const toggleOption = (optionValue: string | number) => {
        const newValue = value.includes(optionValue)
            ? value.filter((v) => v !== optionValue)
            : [...value, optionValue];
        onChange(newValue);
    };

    const removeOption = (optionValue: string | number, e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(value.filter((v) => v !== optionValue));
    };

    const clearAll = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange([]);
    };

    const getSelectedOptions = () => {
        return value
            .map((v) => ({
                value: v,
                label: options.find((o) => o.value === v)?.label,
            }))
            .filter(
                (item): item is { value: string | number; label: string } =>
                    typeof item.label === 'string'
            );
    };

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            {label && (
                <label className="block text-xs font-medium text-slate-600 mb-1">
                    {label}
                </label>
            )}

            {/* Trigger Button */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full min-h-[42px] px-3 py-2 flex items-center justify-between gap-2 border rounded-lg text-sm transition-colors ${isOpen
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-slate-200 hover:border-slate-300'
                    } bg-white`}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <div className="flex-1 flex flex-wrap gap-1 min-w-0">
                    {value.length === 0 ? (
                        <span className="text-slate-400">{placeholder}</span>
                    ) : value.length <= 2 ? (
                        getSelectedOptions().map(({ label, value: selectedValue }) => (
                            <span
                                key={selectedValue}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full"
                            >
                                {label}
                                <button
                                    type="button"
                                    onClick={(e) => removeOption(selectedValue, e)}
                                    className="hover:bg-primary/20 rounded-full p-0.5"
                                >
                                    <X size={12} />
                                </button>
                            </span>
                        ))
                    ) : (
                        <span className="text-slate-700">
                            {value.length} selected
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {value.length > 0 && (
                        <button
                            type="button"
                            onClick={clearAll}
                            className="p-1 hover:bg-slate-100 rounded"
                            aria-label="Clear all"
                        >
                            <X size={14} className="text-slate-400" />
                        </button>
                    )}
                    <ChevronDown
                        size={16}
                        className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''
                            }`}
                    />
                </div>
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
                    {/* Search */}
                    {searchable && (
                        <div className="p-2 border-b border-slate-100">
                            <div className="relative">
                                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    ref={searchRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    placeholder="Search..."
                                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                />
                            </div>
                        </div>
                    )}

                    {/* Options */}
                    <div className="max-h-48 overflow-y-auto" role="listbox" aria-multiselectable="true">
                        {filteredOptions.length === 0 ? (
                            <div className="px-3 py-4 text-sm text-slate-400 text-center">
                                No options found
                            </div>
                        ) : (
                            filteredOptions.map((option) => {
                                const isSelected = value.includes(option.value);
                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => toggleOption(option.value)}
                                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${isSelected
                                            ? 'bg-primary/5 text-primary'
                                            : 'hover:bg-slate-50 text-slate-700'
                                            }`}
                                        role="option"
                                        aria-selected={isSelected}
                                    >
                                        <div
                                            className={`w-4 h-4 rounded border flex items-center justify-center ${isSelected
                                                ? 'bg-primary border-primary'
                                                : 'border-slate-300'
                                                }`}
                                        >
                                            {isSelected && <Check size={12} className="text-white" />}
                                        </div>
                                        <span className="flex-1">{option.label}</span>
                                        {option.count !== undefined && (
                                            <span className="text-xs text-slate-400">
                                                ({option.count})
                                            </span>
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>

                    {/* Footer */}
                    {value.length > 0 && (
                        <div className="px-3 py-2 border-t border-slate-100 bg-slate-50">
                            <button
                                type="button"
                                onClick={clearAll}
                                className="text-xs text-primary hover:text-primary-dark font-medium"
                            >
                                Clear all ({value.length})
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MultiSelect;
